import { WIRED_DEVICE_TYPES } from 'helpers/globals'

export default {
  definitions: {
    location: {
      type: 'object',
      additionalProperties: false,
      properties: {
        lat: { type: 'number' },
        lng: { type: 'number' }
      },
      required: ['lat', 'lng']
    },
    allIds: {
      type: 'array',
      items: { type: 'number' }
    },
    peripheral: {
      type: 'object',
      additionalProperties: false,
      properties: {
        allIds: { $ref: '#/definitions/allIds' },
        byId: {
          type: 'object',
          additionalProperties: false,
          patternProperties: {
            "^[0-9]+$": {
              type: 'object',
              additionalProperties: false,
              properties: {
                id: { type: 'number' },
                customType: {
                  oneOf: [
                    { type: 'boolean' },
                    { type: 'null' }
                  ]
                },
                protocol: {
                  oneOf: [
                    { type: 'string' },
                    { type: 'null' }
                  ]
                },
                type: { type: 'string' },
                wireType: { type: 'string' },
                wireShielded: { type: 'boolean' }
              },
              required: ['id', 'type', 'wireType', 'wireShielded']
            }
          }
        }
      }
    },
    wireDevice: {
      type: 'object',
      additionalProperties: false,
      properties: {
        id: { type: 'number' },
        type: {
          type: 'string',
          enum: ['camera', 'doorController', 'door', 'networkCloset', 'wiredDevice']
        }
      }
    }
  },
  type: 'object',
  properties: {
    cameras: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          notes: { type: 'string' },
          imagerSize: { type: 'number' },
          distance: { type: 'number' },
          horizontalAngleOfView: { type: 'number' },
          imagerCount: { type: 'number' },
          horizontalResolution: { type: 'number' },
          verticalResolution: { type: 'number' },
          cameraHeight: { type: 'number' },
          sceneHeight: { type: 'number' },
          tilt: { type: 'number' },
          fovMode: { type: 'string' },
          unitOfMeasurement: {
            oneOf: [
              { type: 'string' },
              { type: 'null' }
            ]
          },
          heading: { type: 'number' },
          backgroundPosition: {
            type: 'object',
            additionalProperties: false,
            properties: {
              backgroundPositionX: { type: 'number' },
              backgroundPositionY: { type: 'number' }
            },
            required: ['backgroundPositionX', 'backgroundPositionY']
          },
          sceneName: {
            type: 'string',
            enum: ['Intersection', 'Doorway', 'Hallway', 'Indoor Room',
              'Lobby', 'Parking Lot', 'Vehicle Entrance', '360', '180', 'Blank']
          },
          streetViewActive: { type: 'boolean' },
          userUploadedSceneActive: { type: 'boolean' },
          userSelectedGeneralScene: { type: 'boolean' },
          userUploadedSceneUrl: { type: 'string' },
          location: { $ref: '#/definitions/location' },
          active: { type: 'boolean' },
          name: { type: 'string' },
          locked: { type: 'boolean' },
          id: { type: 'number' },
          streetViewUrl: { type: 'string' },
          streetViewAvailable: { type: 'boolean' },
          modelId: { type: 'number' },
          modelAovId: { type: 'number' },
          displayIrLine: { type: 'boolean' },
          dragged: {
            oneOf: [
              { type: 'boolean' },
              { type: 'null' }
            ]
          },
          distanceToTarget: { type: 'number' },
          ppfNightMode: {
            type: 'string',
            enum: ['infrared', 'superLowLight', 'conventional']
          },
          userSelectedPpfNightMode: { type: 'boolean' },
          color: {
            type: 'string',
            enum: ['blue', 'green', 'red', 'orange', 'purple', 'grey']
          },
          imagerId: {
            oneOf: [
              { type: 'number' },
              { type: 'null' }
            ]
          },
          isFixedImager: {
            oneOf: [
              { type: 'boolean' },
              { type: 'null' }
            ]
          },
          isPtzImager: {
            oneOf: [
              { type: 'boolean' },
              { type: 'null' }
            ]
          },
          isMultiImager: {
            oneOf: [
              { type: 'boolean' },
              { type: 'null' }
            ]
          },
          lensIds: {
            oneOf: [
              { type: 'array', items: { type: 'number' } },
              { type: 'null' }
            ]
          },
          fixedLensIds: {
            oneOf: [
              { type: 'array', items: { type: 'number' } },
              { type: 'null' }
            ]
          }
        },
        required: ['notes', 'imagerSize', 'distance', 'horizontalAngleOfView', 'imagerCount', 'horizontalResolution', 'verticalResolution',
          'cameraHeight', 'sceneHeight', 'tilt', 'fovMode', 'heading', 'backgroundPosition', 'sceneName',
          'streetViewActive', 'userSelectedGeneralScene', 'location', 'active', 'name', 'locked', 'id', 'displayIrLine'],
        dependencies: {
          modelAovId: ['modelId']
        }
      }
    },
    currentEntity: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: ['map', 'camera', 'circle', 'polygon', 'polyline',
          'rectangle', 'label', 'floorPlan', 'wall', 'networkCloset', 'wiredDevice']
        },
        id: { type: 'integer' }
      },
      required: ['type']
    },
    active: { type: 'boolean' },
    ui: {
      type: 'object',
      additionalProperties: false,
      properties: {
        sceneSettingsPanelOpen: { type: 'boolean' },
        simulatedViewPanelOpen: { type: 'boolean' },
        simulatedPersonPanelOpen: { type: 'boolean' },
        advancedPanelOpen: { type: 'boolean' },
        blindSpotPanelOpen: { type: 'boolean' },
        cameraDescriptionPanelOpen: { type: 'boolean' },
        sidebarView: { type: 'string' },
        showCameraBlindspot: { type: 'boolean' },
        showAnalyticZones: { type: 'boolean' },
        cameraViewsChoice: { type: 'string' },
        showWallBlindspots: { type: 'boolean' },
        viewOptionsPanelOpen: { type: 'boolean' },
        isImperial: { type: 'boolean' },
        showSimpleCameraIcon: { type: 'boolean' },
        modal: {
          oneOf: [
            { type: 'string' },
            { type: 'null' }
          ]
        },
        ppfHover: { type: 'object' },
        showSideBar: { type: 'boolean' },
        showBottomInputRow: { type: 'boolean' },
        titleChanged: { type: 'boolean' },
        scaling: { type: 'boolean' },
        floorPlanScaleID: {
          oneOf: [
            { type: 'number' },
            { type: 'null' }
          ]
        },
        tutorialVideoPanelOpen: { type: 'boolean' },
        scalingVideoPanelOpen: { type: 'boolean' },
        deviceLimitType: {
          oneOf: [
            { type: 'boolean' },
            { type: 'null' }
          ]
        },
        hideDiscontinuedModels: { type: 'boolean' },
        hideNdaaNonCompliantModels: { type: 'boolean' },
        drawingWall: { type: 'boolean' },
        measurementTool: { type: 'boolean' },
        drawingMeasurement: { type: 'boolean' },
        wallVideoPanelOpen: { type: 'boolean' },
        mapLoaded: { type: 'boolean' },
        wiringPanelOpen: { type: 'boolean' },
        drawingWire: { type: 'boolean' },
        wiringVideoPanelOpen: { type: 'boolean' },
        wiredDeviceVideoPanelOpen: { type: 'boolean' },
        offlineTimeLimit: { type: 'boolean' },
        doorControllerSummaryPanelOpen: { type: 'boolean' },
        doorDetailsPanelOpen: { type: 'boolean' },
        showSceneSelector: {
          oneOf: [
            { type: 'boolean' },
            { type: 'null' }
          ]
        }
      },
      required: ['sceneSettingsPanelOpen', 'simulatedViewPanelOpen', 'simulatedPersonPanelOpen', 'cameraDescriptionPanelOpen', 'sidebarView', 'ppfHover']
    },
    createdAt: { type: 'number' },
    updatedAt: { type: 'number' },
    zoom: { type: 'number' },
    location: {
      oneOf: [
        { $ref: '#/definitions/location' },
        { type: 'null' }
      ]
    },
    mapType: {
      type: 'string',
      enum: ['blank', 'satellite', 'roadmap']
    },
    title: { type: 'string' },
    map: {
      type: 'object',
      additionalProperties: false,
      properties: {
        currentMarkup: { type: 'number' },
        floorPlans: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              id: { type: 'number' },
              imageSrc: { type: 'string' },
              imageSize: {
                type: 'object',
                additionalProperties: false,
                properties: {
                  width: { type: 'number' },
                  height: { type: 'number' }
                },
                required: ['width', 'height']
              },
              location: {
                type: 'object',
                additionalProperties: false,
                properties: {
                  nw: { $ref: '#/definitions/location' },
                  se: { $ref: '#/definitions/location' }
                },
                required: ['nw', 'se']
              },
              locked: { type: 'boolean' },
              name: {
                oneOf: [
                  { type: 'string' },
                  { type: 'null' }
                ]
              },
              opacity: {
                oneOf: [
                  { type: 'number' },
                  { type: 'null' }
                ]
              },
              scaled: {
                oneOf: [
                  { type: 'boolean' },
                  { type: 'null' }
                ]
              }
            },
            required: ['id', 'imageSrc', 'imageSize', 'location']
          }
        },
        markups: {
          type: 'array',
          items: {
            oneOf: [
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  type: {
                    type: 'string',
                    enum: ['circle']
                  },
                  locked: { type: 'boolean' },
                  location: { $ref: '#/definitions/location' },
                  radius: { type: 'number' },
                  strokeColor: { type: 'string' },
                  fillColor: { type: 'string' },
                  id: { type: 'number' }
                },
                required: ['type', 'locked', 'location', 'radius', 'strokeColor', 'fillColor', 'id']
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  type: {
                    type: 'string',
                    enum: ['polygon']
                  },
                  locked: { type: 'boolean' },
                  location: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        lat: { type: 'number' },
                        lng: { type: 'number' }
                      },
                      required: ['lat', 'lng']
                    }
                  },
                  strokeColor: { type: 'string' },
                  fillColor: { type: 'string' },
                  id: { type: 'number' }
                },
                required: ['type', 'locked', 'location', 'strokeColor', 'fillColor', 'id']
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  type: {
                    type: 'string',
                    enum: ['polyline']
                  },
                  locked: { type: 'boolean' },
                  location: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        lat: { type: 'number' },
                        lng: { type: 'number' }
                      },
                      required: ['lat', 'lng']
                    }
                  },
                  strokeColor: { type: 'string' },
                  fillColor: { type: 'string' },
                  id: { type: 'number' }
                },
                required: ['type', 'locked', 'location', 'strokeColor', 'id']
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  type: {
                    type: 'string',
                    enum: ['wall']
                  },
                  locked: { type: 'boolean' },
                  location: {
                    type: 'array',
                    items: {
                      type: 'object',
                      additionalProperties: false,
                      properties: {
                        lat: { type: 'number' },
                        lng: { type: 'number' }
                      },
                      required: ['lat', 'lng']
                    }
                  },
                  strokeColor: { type: 'string' },
                  fillColor: { type: 'string' },
                  id: { type: 'number' }
                },
                required: ['type', 'locked', 'location', 'strokeColor', 'id']
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  type: {
                    type: 'string',
                    enum: ['rectangle']
                  },
                  locked: { type: 'boolean' },
                  location: {
                    type: 'object',
                    additionalProperties: false,
                    properties: {
                      north: { type: 'number' },
                      south: { type: 'number' },
                      east: { type: 'number' },
                      west: { type: 'number' }
                    }
                  },
                  strokeColor: { type: 'string' },
                  fillColor: { type: 'string' },
                  id: { type: 'number' }
                },
                required: ['type', 'locked', 'location', 'strokeColor', 'fillColor', 'id']
              },
              {
                type: 'object',
                additionalProperties: false,
                properties: {
                  type: {
                    type: 'string',
                    enum: ['label']
                  },
                  locked: { type: 'boolean' },
                  location: { $ref: '#/definitions/location' },
                  text: { type: 'string' },
                  dimensions: {
                    baseWidth: { type: 'number' },
                    baseHeight: { type: 'number' },
                    baseFontSize: { type: 'number' }
                  },
                  strokeColor: { type: 'string' },
                  fillColor: { type: 'string' },
                  size: { type: 'number' },
                  id: { type: 'number' }
                },
                required: ['type', 'locked', 'location', 'text', 'dimensions', 'strokeColor', 'fillColor', 'id']
              }
            ]
          }
        }
      }
    },
    entities: {
      type: 'object',
      additionalProperties: false,
      properties: {
        doors: {
          type: 'object',
          additionalProperties: false,
          properties: {
            allIds: { $ref: '#/definitions/allIds' },
            byId: {
              type: 'object',
              additionalProperties: false,
              patternProperties: {
                "^[0-9]+$": {
                  type: 'object',
                  additionalProperties: 'false',
                  properties: {
                    id: { type: 'number' },
                    color: { type: 'string' },
                    controllerId: { type: 'number' },
                    location: { $ref: '#/definitions/location' },
                    locked: { type: 'boolean' },
                    name: { type: 'string' },
                    notes: { type: 'string' },
                    // peripherals
                    auxiliaryInputs: { $ref: '#/definitions/allIds' },
                    auxiliaryOutputs: { $ref: '#/definitions/allIds' },
                    contacts: { $ref: '#/definitions/allIds' },
                    doorOpeners: { $ref: '#/definitions/allIds' },
                    locks: { $ref: '#/definitions/allIds' },
                    readers: { $ref: '#/definitions/allIds' },
                    rexs: { $ref: '#/definitions/allIds' },
                    tampers: { $ref: '#/definitions/allIds' }
                  },
                  required: ['id', 'color', 'controllerId', 'location', 'locked', 'name', 'notes', 'auxiliaryInputs', 'auxiliaryOutputs', 'contacts', 'doorOpeners', 'locks', 'readers', 'rexs', 'tampers']
                }
              },
            }
          }
        },
        doorControllers: {
          type: 'object',
          additionalProperties: false,
          properties: {
            allIds: { $ref: '#/definitions/allIds' },
            byId: {
              type: 'object',
              additionalProperties: false,
              patternProperties: {
                "^[0-9]+$": {
                  type: 'object',
                  additionalProperties: 'false',
                  properties: {
                    id: { type: 'number' },
                    color: { type: 'string' },
                    doorIds: { $ref: '#/definitions/allIds' },
                    dropLength: { type: 'number' },
                    enclosure: { type: 'string' },
                    expansionHeadingCounter: { type: 'number' },
                    expansionIds: { $ref: '#/definitions/allIds' },
                    headingCounter: { type: 'number' },
                    location: { $ref: '#/definitions/location' },
                    locked: { type: 'boolean' },
                    name: { type: 'string' },
                    notes: { type: 'string' },
                    dragged: {
                      oneOf: [
                        { type: 'boolean' },
                        { type: 'null' }
                      ]
                    },
                    isExpansion: {
                      oneOf: [
                        { type: 'boolean' },
                        { type: 'null' }
                      ]
                    },
                    parentId: {
                      oneOf: [
                        { type: 'number' },
                        { type: 'null' }
                      ]
                    }
                  },
                  required: ['id', 'color', 'doorIds', 'dropLength', 'enclosure', 'expansionHeadingCounter', 'expansionIds', 'headingCounter', 'location', 'locked', 'name', 'notes']
                }
              },
            }
          }
        },
        networkClosets: {
          type: 'object',
          additionalProperties: false,
          properties: {
            allIds: { $ref: '#/definitions/allIds' },
            byId: {
              type: 'object',
              additionalProperties: false,
              patternProperties: {
                "^[0-9]+$": {
                  type: 'object',
                  additionalProperties: 'false',
                  properties: {
                    id: { type: 'number' },
                    color: { type: 'string' },
                    dropLength: { type: 'number' },
                    fillColor: { type: 'string' },
                    location: { $ref: '#/definitions/location' },
                    locked: { type: 'boolean' },
                    name: { type: 'string' },
                    notes: { type: 'string' },
                    dragged: {
                      oneOf: [
                        { type: 'boolean' },
                        { type: 'null' }
                      ]
                    }
                  },
                  required: ['id', 'color', 'dropLength', 'fillColor', 'location', 'locked', 'name', 'notes']
                }
              },
            }
          }
        },
        wiredDevices: {
          type: 'object',
          additionalProperties: false,
          properties: {
            allIds: { $ref: '#/definitions/allIds' },
            byId: {
              type: 'object',
              additionalProperties: false,
              patternProperties: {
                "^[0-9]+$": {
                  type: 'object',
                  additionalProperties: 'false',
                  properties: {
                    id: { type: 'number' },
                    color: { type: 'string' },
                    location: { $ref: '#/definitions/location' },
                    locked: { type: 'boolean' },
                    manufacturer: { type: 'string' },
                    model: { type: 'string' },
                    name: { type: 'string' },
                    nameTouched: { type: 'boolean' },
                    notes: { type: 'string' },
                    dragged: {
                      oneOf: [
                        { type: 'boolean' },
                        { type: 'null' }
                      ]
                    },
                    type: {
                      type: 'string',
                      enum: WIRED_DEVICE_TYPES.map(choice => choice.value)
                    }
                  },
                  required: ['id', 'color', 'location', 'locked', 'manufacturer', 'model', 'name', 'nameTouched', 'notes', 'type']
                }
              }
            }
          }
        },
        wires: {
          type: 'object',
          additionalProperties: false,
          properties: {
            allIds: { $ref: '#/definitions/allIds' },
            byId: {
              type: 'object',
              additionalProperties: false,
              patternProperties: {
                "^[0-9]+$": {
                  type: 'object',
                  additionalProperties: false,
                  properties: {
                    id: { type: 'number' },
                    location: {
                      type: 'array',
                      items: {
                        type: 'object',
                        additionalProperties: false,
                        properties: {
                          lat: { type: 'number' },
                          lng: { type: 'number' }
                        },
                        required: ['lat', 'lng']
                      }
                    },
                    locked: { type: 'boolean' },
                    showTooltip: {
                      oneOf: [
                        { type: 'string' },
                        { type: 'null' }
                      ]
                    },
                    strokeColor: { type: 'string' },
                    start: { $ref: '#/definitions/wireDevice' },
                    end: { $ref: '#/definitions/wireDevice' }
                  },
                  required: ['id', 'location', 'locked', 'strokeColor', 'start', 'end']
                }
              }
            }
          }
        },
        // peripherals
        auxiliaryInputs: { $ref: '#/definitions/peripheral' },
        auxiliaryOutputs: { $ref: '#/definitions/peripheral' },
        contacts: { $ref: '#/definitions/peripheral' },
        doorOpeners: { $ref: '#/definitions/peripheral' },
        locks: { $ref: '#/definitions/peripheral' },
        readers: { $ref: '#/definitions/peripheral' },
        rexs: { $ref: '#/definitions/peripheral' },
        tampers: { $ref: '#/definitions/peripheral' }
      },
      required: ['doors', 'doorControllers', 'networkClosets', 'wiredDevices', 'networkClosets', 'auxiliaryInputs', 'auxiliaryOutputs', 'contacts', 'doorOpeners', 'locks', 'readers', 'rexs', 'tampers']
    }
  },
  required: ['cameras', 'currentEntity', 'active', 'ui', 'createdAt', 'updatedAt', 'zoom', 'location', 'mapType', 'title', 'map', 'entities']
}



// WEBPACK FOOTER //
// ./src/schemas/v2/index.js